/**
 * Carrega vários Patients em um (ou poucos) Bundle FHIR R5 type=batch.
 * Substitui N GETs paralelos — falha parcial (404 etc.) não derruba o lote.
 * @module GetPatientsBatch
 */

import type { Bundle, BundleEntry, Patient } from "fhir/r5";
import { endpoints, oids } from "../../../configs/Settings";
import { getAuthToken } from "@/services/auth/OAuthApplication";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAxios } from "../../../utils/useAxios";

/** Limite de entries por Bundle para evitar payload/timeout excessivos. */
const PATIENT_BATCH_CHUNK_SIZE = 50;

/** Timeout por POST (chunks grandes no MPI). */
const PATIENT_BATCH_TIMEOUT_MS = 60000;

interface GetPatientsBatchProps {
  /** Identificadores sem prefixo OID (ex.: CPF), como em GetPatient. */
  identifiers: string[];
}

function isPatient(resource: unknown): resource is Patient {
  return (
    typeof resource === "object" &&
    resource !== null &&
    "resourceType" in resource &&
    (resource as { resourceType?: string }).resourceType === "Patient"
  );
}

function chunkIdentifiers(identifiers: string[]): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < identifiers.length; i += PATIENT_BATCH_CHUNK_SIZE) {
    chunks.push(identifiers.slice(i, i + PATIENT_BATCH_CHUNK_SIZE));
  }
  return chunks;
}

/** Monta Bundle batch com GET Patient/{oids.cpf}-{id} por entry. */
function buildPatientBatchBundle(identifiers: string[]): Bundle {
  const entry: BundleEntry[] = identifiers.map((rawId) => {
    const identifier = rawId.trim();
    return {
      request: {
        method: "GET",
        url: `Patient/${oids.cpf}-${identifier}`,
      },
    };
  });

  return {
    resourceType: "Bundle",
    type: "batch",
    entry,
  };
}

/**
 * Extrai Patients da resposta batch-response.
 * Entries sem resource (ex.: 404) são ignoradas — mesma semântica do allSettled.
 */
function extractPatientsFromBatchResponse(bundle: unknown): Patient[] {
  const entries = (bundle as Bundle | undefined)?.entry;
  if (!Array.isArray(entries)) return [];

  const patients: Patient[] = [];
  for (const item of entries) {
    const resource = item?.resource;
    if (isPatient(resource)) {
      patients.push(resource);
    }
  }
  return patients;
}

async function postPatientBatchChunk(
  identifiers: string[],
): Promise<Patient[]> {
  if (!identifiers.length) return [];

  const url = `${endpoints.MPIFHIR}`;
  const body = buildPatientBatchBundle(identifiers);
  const headers = {
    Authorization: await getAuthToken(),
    "Content-Type": "application/fhir+json",
    Accept: "application/fhir+json",
  };

  const response = await useAxios.post(url, body, {
    headers,
    timeout: PATIENT_BATCH_TIMEOUT_MS,
  });

  if (response.status !== HTTPResponseStatus.Success) {
    throw new Error(`GetPatientsBatch: ${response.status}`);
  }

  return extractPatientsFromBatchResponse(response.data);
}

/**
 * POST Bundle batch no MPI — 1 HTTP (ou N/chunkSize) em vez de N GETs.
 */
export async function GetPatientsBatch({
  identifiers,
}: GetPatientsBatchProps): Promise<{
  status: typeof HTTPResponseStatus.Success;
  data: Patient[];
}> {
  const uniqueIds = [
    ...new Set(
      (identifiers || [])
        .map((id) => id?.trim())
        .filter((id): id is string => Boolean(id)),
    ),
  ];

  if (!uniqueIds.length) {
    return { status: HTTPResponseStatus.Success, data: [] };
  }

  try {
    const chunks = chunkIdentifiers(uniqueIds);

    const chunkResults = await Promise.allSettled(
      chunks.map((chunk) => postPatientBatchChunk(chunk)),
    );

    const patients: Patient[] = [];
    for (const result of chunkResults) {
      if (result.status === "fulfilled") {
        patients.push(...result.value);
      } else {
        console.error("GetPatientsBatch chunk failed", result.reason);
      }
    }

    if (!patients.length && chunkResults.some((r) => r.status === "rejected")) {
      throw new Error("GetPatientsBatch: all chunks failed");
    }

    return { status: HTTPResponseStatus.Success, data: patients };
  } catch (error) {
    console.error("GetPatientsBatch ", error);
    throw new Error();
  }
}
